// @flow
import * as React from 'react';
import * as Immutable from 'immutable';
import { useTable, useFlexLayout } from 'react-table-7';
import { Box, Heading, Position, Text } from '@locus-taxy/neo-ui';

import NoRows from './NoRows';

export type Column = {
    id: string,
    accessor?: string,
    headerText: string,
    Header?: React.ElementType<any>,
    Cell?: React.ElementType<any>
};

type Props = {
    columns: Array<Column>,
    data: Immutable.List<any>,
    NoRows?: () => React.Node
};

const DefaultNoRows = () => (
    <Box m={8}>
        <NoRows />
    </Box>
);

const DataTable = ({
    columns,
    data,
    NoRows = DefaultNoRows
}: Props): React.Node => {
    const tableColumns = React.useMemo(
        () =>
            columns.map(({ headerText, ...columnProps }) => ({
                ...columnProps,
                Header: columnProps.Header || (
                    <Box p={3}>
                        <Heading size="regular" textAlign="left">
                            {headerText}
                        </Heading>
                    </Box>
                )
            })),
        [columns]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow
    } = useTable({
        columns: tableColumns,
        data,
        useFlexLayout,
        defaultColumn: {
            Cell: props => {
                return (
                    <Box p={3}>
                        <Text size="regular">{props.cell.value}</Text>
                    </Box>
                );
            }
        }
    });

    return (
        <Box
            height="100%"
            overflow="auto"
            borderWidth="thin"
            borderStyle="solid"
            borderColor="grey.200"
            backgroundColor="white"
            sx={{
                '&::-webkit-scrollbar': {
                    borderRadius: '20px',
                    height: '10px',
                    width: '10px'
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#b3b3b3',
                    border: '3px solid #fff',
                    borderRadius: '20px'
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                    borderRadius: '20px'
                }
            }}
        >
            <Box as="table" width="100%" height="100%" {...getTableProps()}>
                <Position as="thead" position="sticky" top={0} zIndex={1}>
                    {headerGroups.map((headerGroup, idx) => (
                        <Box
                            key={idx}
                            as="tr"
                            minHeight="40px"
                            {...headerGroup.getHeaderGroupProps()}
                        >
                            {headerGroup.headers.map((column, colIdx) => (
                                <Box
                                    key={colIdx}
                                    as="th"
                                    backgroundColor="grey.100"
                                    color="grey.600"
                                    borderRight="0"
                                    borderBottomWidth="none"
                                    boxShadow="inset 0px -1px 0px #E7E9EE"
                                    {...column.getHeaderProps()}
                                >
                                    {column.render('Header')}
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Position>

                <Box as="tbody" {...getTableBodyProps()}>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={tableColumns.length}>
                                <NoRows />
                            </td>
                        </tr>
                    ) : (
                        rows.map((row, idx) => {
                            prepareRow(row);

                            return (
                                <Box key={idx} as="tr" {...row.getRowProps()}>
                                    {row.cells.map((cell, cellIdx) => {
                                        return (
                                            <Box
                                                key={cellIdx}
                                                as="td"
                                                borderRightWidth={1}
                                                borderRightStyle="solid"
                                                borderRightColor="transparent"
                                                borderBottomWidth={1}
                                                borderBottomStyle="solid"
                                                borderBottomColor="grey.200"
                                                {...cell.getCellProps()}
                                            >
                                                {cell.render('Cell')}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            );
                        })
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default DataTable;
